import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';
import { CalendarComponent, CalendarEvent } from '../../../../shared/components/calendar';
import { PaginationComponent } from '../../../../shared/components/pagination';
import { HealthEvent, HealthEventType, HealthEventFormData, HealthEventApi, HealthEventResponse, AppointmentApi, AppointmentResponse } from '../../../../core/interfaces/health-event.interface';
import { HealthEventService } from '../../../../core/services/health-event.service';
import { AppointmentService } from '../../../../core/services/appointment.service';
import { PaymentService } from '../../../../core/services/payment.service';
import { SwalService } from '../../../../core/services/swal.service';
import { ModalService } from '../../../../core/services';
import { CheckoutIframeModalComponent } from '../../../../shared/components/modal/checkout-iframe-modal/checkout-iframe-modal.component';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { Observable, timeout, catchError, of, forkJoin } from 'rxjs';
import { User } from '../../../../core/interfaces/user.interface';
import { LucideAngularModule, UserIcon, MoreVertical } from 'lucide-angular';

@Component({
  selector: 'app-health-tracking',
  standalone: true,
  imports: [CommonModule, FormsModule, BannerComponent, CalendarComponent, PaginationComponent, LucideAngularModule],
  templateUrl: './health-tracking.component.html',
  styleUrls: ['./health-tracking.component.scss']
})
export class HealthTrackingComponent implements OnInit {
  links = [{ label: 'Votre passeport', href: undefined }, { label: 'Suivi Santé', href: undefined }];
  
  readonly UserIcon = UserIcon;
  readonly MoreVerticalIcon = MoreVertical;
  
  // Données des événements
  pastEvents: HealthEvent[] = [];
  upcomingEvents: HealthEvent[] = [];
  allEvents: HealthEvent[] = []; // Pour le calendrier (health-events)
  
  // Utilisateur actuel
  currentUser$: Observable<User | null>;
  currentUser: User | null = null;
  
  // État de chargement
  isLoading = false;
  isLoadingCalendar = false;
  isLoadingList = false;
  error: string | null = null;
  
  // Gestion des erreurs d'images
  imageErrors = new Set<string>();
  
  // Gestion des dropdowns d'options pour chaque événement
  openDropdowns = new Set<string>();
  
  // Calendrier
  currentDate = new Date();
  selectedDate: Date | null = null;
  calendarDays: any[] = [];
  selectedDateEvents: HealthEvent[] = [];
  calendarEvents: CalendarEvent[] = [];
  
  // Filtres
  selectedFilter = 'all';
  activeTab = 'upcoming'; // Par défaut "Agenda de santé"
  activeSection = 'appointments'; // 'appointments', 'vaccines' ou 'services'
  filteredEvents: HealthEvent[] = [];
  
  // Pagination
  currentPage = 1;
  itemsPerPage = 12;
  totalItems = 0;
  totalPages = 0;
  paginationInfo = {
    past: { currentPage: 1, totalPages: 0, totalItems: 0 },
    upcoming: { currentPage: 1, totalPages: 0, totalItems: 0 }
  };
  
  filterTabs = [
    { key: 'upcoming', label: 'Agenda de santé' },
    { key: 'past', label: 'Histoire de santé' }
  ];
  
  // Formulaire d'ajout d'événement
  showAddForm = false;
  newEvent: HealthEventFormData = {
    type: HealthEventType.APPOINTMENT,
    title: '',
    description: '',
    date: new Date(),
    location: '',
    doctor: '',
    amount: 0,
    currency: 'EUR',
    notes: ''
  };
  
  eventTypes = Object.values(HealthEventType);
  eventTypeLabels: { [key in HealthEventType]: string } = {
    [HealthEventType.APPOINTMENT]: 'Rendez-vous',
    [HealthEventType.VACCINATION]: 'Vaccin',
    [HealthEventType.EXAMINATION]: 'Examen',
    [HealthEventType.TREATMENT]: 'Traitement',
    [HealthEventType.MEDICAL_SERVICE]: 'Prestation médicale',
    [HealthEventType.FINANCIAL_SERVICE]: 'Prestation financière',
    [HealthEventType.OTHER]: 'Autre'
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private healthEventService: HealthEventService,
    private appointmentService: AppointmentService,
    private paymentService: PaymentService,
    private swalService: SwalService,
    private modalService: ModalService,
    private store: Store
  ) {
    this.currentUser$ = this.store.select(selectCurrentUser);
  }

  ngOnInit(): void {
    const requestedTab = this.route.snapshot.queryParamMap.get('tab');
    if (requestedTab === 'past' || requestedTab === 'upcoming') {
      this.activeTab = requestedTab;
    }

    // S'abonner aux changements de l'utilisateur actuel
    this.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user?.passportNumber) {
        this.loadCalendarEvents(); // Charger les événements pour le calendrier
        this.loadAppointmentsList(); // Charger les rendez-vous pour la liste
      }
    });
  }

  /**
   * Charge les événements pour le calendrier depuis /health-events/passport/
   */
  private loadCalendarEvents(): void {
    if (!this.currentUser?.passportNumber) {
      console.error('Aucun numéro de passeport disponible');
      return;
    }

    this.isLoadingCalendar = true;

    // Utiliser l'endpoint health-events avec une limite élevée pour récupérer tous les événements
    this.healthEventService.getHealthEvents(this.currentUser.passportNumber, 1, 100)
      .pipe(
        timeout(10000),
        catchError(error => {
          console.error('Erreur lors du chargement des événements du calendrier:', error);
          this.error = 'Erreur lors du chargement des événements du calendrier';
          return of({ data: [], meta: { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 100 } });
        })
      )
      .subscribe({
        next: (response: HealthEventResponse) => {
          console.log('Réponse calendrier reçue:', response);
          try {
            if (response && response.data && Array.isArray(response.data)) {
              this.allEvents = response.data.map((apiEvent: HealthEventApi) => 
                this.transformApiEventToHealthEvent(apiEvent)
              );
              console.log('Événements calendrier transformés:', this.allEvents);
              this.generateCalendar();
              this.calendarEvents = this.convertToCalendarEvents(this.allEvents);
              console.log('Événements calendrier finaux:', this.calendarEvents);
            } else {
              console.warn('Structure de réponse calendrier inattendue:', response);
              this.allEvents = [];
              this.calendarEvents = [];
            }
          } catch (error) {
            console.error('Erreur lors de la transformation des événements du calendrier:', error);
            this.allEvents = [];
            this.calendarEvents = [];
          } finally {
            this.isLoadingCalendar = false;
          }
        }
      });
  }

  /**
   * Charge les rendez-vous pour la liste depuis /passeport/appointments
   */
  private loadAppointmentsList(): void {
    if (!this.currentUser?.passportNumber) {
      console.error('Aucun numéro de passeport disponible');
      return;
    }

    this.isLoadingList = true;
    this.error = null;

    // Charger les rendez-vous passés et à venir en parallèle avec pagination
    const pastAppointments$ = this.healthEventService.getPastAppointments(
      this.currentUser.passportNumber, 
      this.paginationInfo.past.currentPage, 
      this.itemsPerPage
    )
      .pipe(
        timeout(10000),
        catchError(error => {
          console.error('Erreur lors du chargement des rendez-vous passés:', error);
          this.error = 'Erreur lors du chargement des rendez-vous passés';
          return of({ data: [], meta: { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 12 } });
        })
      );

    const upcomingAppointments$ = this.healthEventService.getUpcomingAppointments(
      this.currentUser.passportNumber, 
      this.paginationInfo.upcoming.currentPage, 
      this.itemsPerPage
    )
      .pipe(
        timeout(10000),
        catchError(error => {
          console.error('Erreur lors du chargement des rendez-vous à venir:', error);
          this.error = 'Erreur lors du chargement des rendez-vous à venir';
          return of({ data: [], meta: { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 12 } });
        })
      );

    // Combiner les deux requêtes
    forkJoin({
      past: pastAppointments$,
      upcoming: upcomingAppointments$
    }).subscribe({
      next: (responses) => {
        console.log('Rendez-vous liste reçus:', responses);
        try {
          this.processApiResponses(responses.past, responses.upcoming);
        } catch (error) {
          console.error('Erreur lors de la transformation des rendez-vous:', error);
          this.error = 'Erreur lors du traitement des données';
          this.pastEvents = [];
          this.upcomingEvents = [];
          this.filteredEvents = [];
        } finally {
          this.isLoadingList = false;
        }
      },
      error: (error) => {
        console.error('Erreur lors du chargement des rendez-vous:', error);
        this.error = 'Erreur lors du chargement des rendez-vous';
        this.isLoadingList = false;
        this.pastEvents = [];
        this.upcomingEvents = [];
        this.filteredEvents = [];
      }
    });
  }

  /**
   * Méthode publique pour recharger toutes les données (bouton retry)
   */
  loadHealthEvents(): void {
    this.error = null; // Effacer les erreurs précédentes
    this.loadCalendarEvents();
    this.loadAppointmentsList();
  }

  /**
   * Traite les réponses API séparées pour les rendez-vous passés et à venir.
   * On fait confiance à l'API : pas de réorganisation par date côté front.
   */
  private processApiResponses(pastResponse: AppointmentResponse, upcomingResponse: AppointmentResponse): void {
    console.log('Processing API responses:', { pastResponse, upcomingResponse });
    
    this.pastEvents = [];
    this.upcomingEvents = [];

    // Traiter les rendez-vous passés (tels que renvoyés par l'API)
    if (pastResponse && pastResponse.data && Array.isArray(pastResponse.data)) {
      console.log('Processing past appointments:', pastResponse.data.length);
      this.pastEvents = pastResponse.data.map((appointment: AppointmentApi) => 
        this.transformAppointmentToHealthEvent(appointment)
      );
      console.log('Transformed past events:', this.pastEvents.length);
      
      // Stocker les informations de pagination pour les rendez-vous passés
      if (pastResponse.meta) {
        this.paginationInfo.past = {
          currentPage: pastResponse.meta.currentPage,
          totalPages: Math.max(1, pastResponse.meta.totalPages),
          totalItems: pastResponse.meta.totalItems
        };
      } else if (pastResponse.pagination) {
        this.paginationInfo.past = {
          currentPage: pastResponse.pagination.page,
          totalPages: Math.max(1, pastResponse.pagination.totalPages),
          totalItems: pastResponse.pagination.total
        };
      } else {
        // Fallback : au moins une page quand on a des données (pour afficher la pagination dans "Histoire de santé")
        this.paginationInfo.past = {
          currentPage: 1,
          totalPages: 1,
          totalItems: this.pastEvents.length
        };
      }
    }

    // Traiter les rendez-vous à venir (tels que renvoyés par l'API)
    if (upcomingResponse && upcomingResponse.data && Array.isArray(upcomingResponse.data)) {
      console.log('Processing upcoming appointments:', upcomingResponse.data.length);
      this.upcomingEvents = upcomingResponse.data.map((appointment: AppointmentApi) => 
        this.transformAppointmentToHealthEvent(appointment)
      );
      console.log('Transformed upcoming events:', this.upcomingEvents.length);
      
      // Stocker les informations de pagination pour les rendez-vous à venir
      if (upcomingResponse.meta) {
        this.paginationInfo.upcoming = {
          currentPage: upcomingResponse.meta.currentPage,
          totalPages: Math.max(1, upcomingResponse.meta.totalPages),
          totalItems: upcomingResponse.meta.totalItems
        };
      } else if (upcomingResponse.pagination) {
        this.paginationInfo.upcoming = {
          currentPage: upcomingResponse.pagination.page,
          totalPages: Math.max(1, upcomingResponse.pagination.totalPages),
          totalItems: upcomingResponse.pagination.total
        };
      } else {
        this.paginationInfo.upcoming = {
          currentPage: 1,
          totalPages: 1,
          totalItems: this.upcomingEvents.length
        };
      }
    }

    this.processEvents();
  }

  /**
   * Transforme les données de l'API en format utilisé par le composant (méthode legacy)
   */
  private transformAndProcessEvents(apiEvents: HealthEventApi[]): void {
    const now = new Date();
    
    this.pastEvents = [];
    this.upcomingEvents = [];

    apiEvents.forEach(apiEvent => {
      const transformedEvent = this.transformApiEventToHealthEvent(apiEvent);
      
      if (transformedEvent.date < now) {
        this.pastEvents.push(transformedEvent);
      } else {
        this.upcomingEvents.push(transformedEvent);
      }
    });

    this.processEvents();
  }

  /**
   * Transforme un rendez-vous de l'API en HealthEvent
   */
  private transformAppointmentToHealthEvent(appointment: AppointmentApi): HealthEvent {
    console.log('Transforming appointment:', appointment);
    
    // Utiliser startTime pour avoir la date et l'heure complètes, sinon utiliser date
    let appointmentDate: Date;
    if (appointment.startTime) {
      appointmentDate = new Date(appointment.startTime);
    } else {
      appointmentDate = new Date(appointment.date);
    }
    
    // Déterminer le statut basé sur les données de l'API
    let status: 'completed' | 'scheduled' | 'cancelled' | 'pending' | 'pending_payment' | 'confirmed' | 'rejected' = 'scheduled';
    switch (appointment.status) {
      case 'confirmed':
        status = 'confirmed';
        break;
      case 'pending':
        status = 'pending';
        break;
      case 'pending_payment':
        status = 'pending_payment';
        break;
      case 'cancelled':
        status = 'cancelled';
        break;
      case 'completed':
        status = 'completed';
        break;
      case 'rejected':
        status = 'rejected';
        break;
      default:
        // Utiliser startTime pour déterminer si le rendez-vous est passé
        const now = new Date();
        status = appointmentDate < now ? 'completed' : 'scheduled';
    }

    // Utiliser les propriétés plates ou fallback
    const reasonLabel = appointment.reason?.label || '';
    const doctor = appointment.doctor || appointment.professionalName || '';
    const structureName = appointment.structure?.name || appointment.structureName || '';
    const title = appointment.title || `Rendez-vous ${reasonLabel}`;
    const description = appointment.description || 
      `Rendez-vous médical: ${reasonLabel}\nMédecin: ${doctor}\nStructure: ${structureName}\nHeure: ${appointment.startTime} - ${appointment.endTime}\nStatut: ${status === 'confirmed' ? 'Confirmé' : status === 'pending' ? 'En attente' : 'Terminé'}`;

    const transformedEvent = {
      id: appointment._id,
      type: HealthEventType.APPOINTMENT, // Les rendez-vous sont toujours des appointments
      title: title,
      description: description,
      date: appointmentDate,
      status: status,
      location: structureName,
      doctor: doctor,
      doctorPhoto: appointment.doctorPhoto, // Inclure la photo du docteur
      notes: appointment.notes || '',
      createdAt: new Date(appointment.createdAt),
      updatedAt: new Date(appointment.updatedAt),
      professionalName: appointment.professionalName || doctor,
      structureName: structureName,
      passportNumber: appointment.passportNumber,
      appointment: appointment,
      user: appointment.user // Inclure les informations de l'utilisateur
    };
    
    console.log('Transformed appointment event:', transformedEvent);
    return transformedEvent;
  }

  /**
   * Transforme un événement de l'API en HealthEvent
   */
  private transformApiEventToHealthEvent(apiEvent: HealthEventApi): HealthEvent {
    console.log('Transforming API event:', apiEvent);
    
    // Utiliser la date de l'événement principal ou celle de l'appointment (legacy)
    const eventDate = apiEvent.appointment?.date 
      ? new Date(apiEvent.appointment.date) 
      : new Date(apiEvent.date);
    
    // Déterminer le statut basé sur les données de l'API (propriété plate ou legacy)
    let status: 'completed' | 'scheduled' | 'cancelled' | 'pending' | 'pending_payment' | 'confirmed' | 'rejected' = 'scheduled';
    const statusValue = apiEvent.status || apiEvent.appointment?.status;
    if (statusValue) {
      switch (statusValue) {
        case 'confirmed':
          status = 'confirmed';
          break;
        case 'pending':
          status = 'pending';
          break;
        case 'pending_payment':
          status = 'pending_payment';
          break;
        case 'cancelled':
          status = 'cancelled';
          break;
        case 'completed':
          status = 'completed';
          break;
        case 'rejected':
          status = 'rejected';
          break;
        default:
          status = eventDate < new Date() ? 'completed' : 'scheduled';
      }
    } else {
      status = eventDate < new Date() ? 'completed' : 'scheduled';
    }

    // Utiliser les propriétés plates ou fallback sur les propriétés legacy
    const doctor = apiEvent.doctor || apiEvent.professionalName;
    const structureName = apiEvent.structure?.name || apiEvent.structureName;
    const location = structureName;

    const transformedEvent = {
      id: apiEvent._id,
      type: this.mapApiTypeToHealthEventType(apiEvent.type),
      title: apiEvent.title,
      description: apiEvent.description,
      date: eventDate,
      status: status,
      location: location,
      doctor: doctor,
      notes: apiEvent.notes || '',
      createdAt: new Date(apiEvent.createdAt),
      updatedAt: new Date(apiEvent.updatedAt),
      professionalName: apiEvent.professionalName || doctor,
      structureName: structureName,
      passportNumber: apiEvent.passportNumber,
      appointment: apiEvent.appointment || apiEvent, // Stocker l'objet complet pour référence
      user: apiEvent.user // Inclure les informations de l'utilisateur
    };
    
    return transformedEvent;
  }

  /**
   * Mappe le type de l'API vers HealthEventType
   */
  private mapApiTypeToHealthEventType(apiType: string): HealthEventType {
    switch (apiType.toLowerCase()) {
      case 'appointment':
        return HealthEventType.APPOINTMENT;
      case 'vaccination':
        return HealthEventType.VACCINATION;
      case 'examination':
        return HealthEventType.EXAMINATION;
      case 'treatment':
        return HealthEventType.TREATMENT;
      case 'medical_service':
        return HealthEventType.MEDICAL_SERVICE;
      case 'financial_service':
        return HealthEventType.FINANCIAL_SERVICE;
      default:
        return HealthEventType.OTHER;
    }
  }

  /**
   * Traite les événements après transformation
   */
  private processEvents(): void {
    this.allEvents = [...this.pastEvents, ...this.upcomingEvents];
    this.generateCalendar();
    this.filterEvents();
    this.calendarEvents = this.convertToCalendarEvents(this.allEvents);
  }

  private loadMockData(): void {
    const now = new Date();
    
    // Événements passés (derniers 30 jours)
    this.pastEvents = [
      {
        id: '1',
        type: HealthEventType.APPOINTMENT,
        title: 'Consultation cardiologie',
        description: 'Contrôle de routine',
        date: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Hôpital Saint-Antoine',
        doctor: 'Dr. Martin',
        createdAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      },
      {
        id: '2',
        type: HealthEventType.FINANCIAL_SERVICE,
        title: 'Remboursement consultation',
        description: 'Remboursement sécurité sociale',
        date: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        status: 'completed',
        amount: 25.50,
        currency: 'EUR',
        createdAt: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)
      },
      {
        id: '3',
        type: HealthEventType.EXAMINATION,
        title: 'Prise de sang',
        description: 'Analyses de routine',
        date: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Laboratoire Pasteur',
        doctor: 'Dr. Dubois',
        createdAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
      },
      {
        id: '4',
        type: HealthEventType.VACCINATION,
        title: 'Vaccin COVID-19',
        description: 'Rappel de vaccination',
        date: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Centre de vaccination',
        doctor: 'Dr. Moreau',
        createdAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
      },
      {
        id: '8',
        type: HealthEventType.APPOINTMENT,
        title: 'Consultation dentaire',
        description: 'Contrôle dentaire semestriel',
        date: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Cabinet dentaire Dr. Petit',
        doctor: 'Dr. Petit',
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)
      },
      {
        id: '9',
        type: HealthEventType.EXAMINATION,
        title: 'Échographie abdominale',
        description: 'Examen d\'imagerie médicale',
        date: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Centre d\'imagerie médicale',
        doctor: 'Dr. Leroy',
        createdAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000)
      },
      {
        id: '10',
        type: HealthEventType.TREATMENT,
        title: 'Séance kinésithérapie',
        description: 'Rééducation du genou',
        date: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Cabinet de kinésithérapie',
        doctor: 'M. Dubois',
        createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)
      },
      {
        id: '11',
        type: HealthEventType.MEDICAL_SERVICE,
        title: 'Pansement post-opératoire',
        description: 'Soins de plaie',
        date: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Infirmerie locale',
        doctor: 'Infirmière Martin',
        createdAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)
      },
      {
        id: '12',
        type: HealthEventType.FINANCIAL_SERVICE,
        title: 'Remboursement médicaments',
        description: 'Remboursement mutuelle',
        date: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        status: 'completed',
        amount: 45.20,
        currency: 'EUR',
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000)
      },
      {
        id: '13',
        type: HealthEventType.APPOINTMENT,
        title: 'Consultation dermatologie',
        description: 'Contrôle grain de beauté',
        date: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000),
        status: 'completed',
        location: 'Cabinet dermatologie',
        doctor: 'Dr. Bernard',
        createdAt: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000)
      }
    ];

    // Événements à venir (prochains 30 jours)
    this.upcomingEvents = [
      {
        id: '5',
        type: HealthEventType.APPOINTMENT,
        title: 'Consultation ophtalmologie',
        description: 'Contrôle de la vue',
        date: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Clinique des yeux',
        doctor: 'Dr. Bernard',
        createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)
      },
      {
        id: '6',
        type: HealthEventType.EXAMINATION,
        title: 'IRM du genou',
        description: 'Examen d\'imagerie médicale',
        date: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Centre d\'imagerie médicale',
        doctor: 'Dr. Leroy',
        createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)
      },
      {
        id: '7',
        type: HealthEventType.VACCINATION,
        title: 'Vaccin grippe',
        description: 'Vaccination antigrippale',
        date: new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Cabinet médical',
        doctor: 'Dr. Durand',
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
      },
      {
        id: '14',
        type: HealthEventType.APPOINTMENT,
        title: 'Consultation généraliste',
        description: 'Bilan de santé annuel',
        date: new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Cabinet médical',
        doctor: 'Dr. Durand',
        createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)
      },
      {
        id: '15',
        type: HealthEventType.TREATMENT,
        title: 'Séance kinésithérapie',
        description: 'Rééducation du genou - suite',
        date: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Cabinet de kinésithérapie',
        doctor: 'M. Dubois',
        createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)
      },
      {
        id: '16',
        type: HealthEventType.EXAMINATION,
        title: 'Radiographie thorax',
        description: 'Examen pulmonaire',
        date: new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Centre d\'imagerie médicale',
        doctor: 'Dr. Leroy',
        createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)
      },
      {
        id: '17',
        type: HealthEventType.APPOINTMENT,
        title: 'Consultation cardiologie',
        description: 'Suivi post-opératoire',
        date: new Date(now.getTime() + 8 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Hôpital Saint-Antoine',
        doctor: 'Dr. Martin',
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
      },
      {
        id: '18',
        type: HealthEventType.VACCINATION,
        title: 'Vaccin tétanos',
        description: 'Rappel de vaccination',
        date: new Date(now.getTime() + 12 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Cabinet médical',
        doctor: 'Dr. Durand',
        createdAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000)
      },
      {
        id: '19',
        type: HealthEventType.TREATMENT,
        title: 'Séance kinésithérapie',
        description: 'Rééducation du genou - fin',
        date: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Cabinet de kinésithérapie',
        doctor: 'M. Dubois',
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)
      },
      {
        id: '20',
        type: HealthEventType.EXAMINATION,
        title: 'Échographie cardiaque',
        description: 'Examen cardiaque spécialisé',
        date: new Date(now.getTime() + 18 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Centre d\'imagerie médicale',
        doctor: 'Dr. Leroy',
        createdAt: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)
      },
      {
        id: '21',
        type: HealthEventType.APPOINTMENT,
        title: 'Consultation ORL',
        description: 'Contrôle auditif',
        date: new Date(now.getTime() + 22 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        location: 'Cabinet ORL',
        doctor: 'Dr. Moreau',
        createdAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      },
      {
        id: '22',
        type: HealthEventType.FINANCIAL_SERVICE,
        title: 'Remboursement consultation',
        description: 'Remboursement mutuelle',
        date: new Date(now.getTime() + 25 * 24 * 60 * 60 * 1000),
        status: 'scheduled',
        amount: 30.00,
        currency: 'EUR',
        createdAt: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000)
      }
    ];
  }

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    if (!this.showAddForm) {
      this.resetForm();
    }
  }

  resetForm(): void {
    this.newEvent = {
      type: HealthEventType.APPOINTMENT,
      title: '',
      description: '',
      date: new Date(),
      location: '',
      doctor: '',
      amount: 0,
      currency: 'EUR',
      notes: ''
    };
  }

  addEvent(): void {
    if (!this.newEvent.title || !this.newEvent.description) {
      alert('Veuillez remplir le titre et la description');
      return;
    }

    const newEvent: HealthEvent = {
      id: Date.now().toString(),
      ...this.newEvent,
      status: this.newEvent.date > new Date() ? 'scheduled' : 'completed',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    if (newEvent.status === 'scheduled') {
      this.upcomingEvents.push(newEvent);
    } else {
      this.pastEvents.push(newEvent);
    }

    // Mettre à jour la liste complète et régénérer le calendrier
    this.allEvents = [...this.pastEvents, ...this.upcomingEvents];
    this.generateCalendar();
    this.filterEvents();

    this.resetForm();
    this.showAddForm = false;
  }

  getEventTypeLabel(type: HealthEventType): string {
    return this.eventTypeLabels[type];
  }

  getEventTypeIcon(type: HealthEventType): string {
    const icons: { [key in HealthEventType]: string } = {
      [HealthEventType.APPOINTMENT]: 'calendar',
      [HealthEventType.VACCINATION]: 'syringe',
      [HealthEventType.EXAMINATION]: 'search',
      [HealthEventType.TREATMENT]: 'heart',
      [HealthEventType.MEDICAL_SERVICE]: 'stethoscope',
      [HealthEventType.FINANCIAL_SERVICE]: 'euro',
      [HealthEventType.OTHER]: 'circle'
    };
    return icons[type];
  }

  formatDate(date: Date | string | undefined): string {
    const d = this.toValidDate(date);
    if (!d) return '';
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  }

  formatAmount(amount?: number, currency?: string): string {
    if (!amount) return '';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  }

  // Méthodes du calendrier
  generateCalendar(): void {
    console.log('Generating calendar with events:', this.allEvents);
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    this.calendarDays = [];
    const currentDate = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      const dayEvents = this.getEventsForDate(currentDate);
      this.calendarDays.push({
        date: currentDate.getDate(),
        fullDate: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === month,
        hasEvents: dayEvents.length > 0
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    console.log('Generated calendar days:', this.calendarDays);
  }

  getEventsForDate(date: Date): HealthEvent[] {
    const events = this.allEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
    return events;
  }

  previousMonth(): void {
    this.currentDate.setMonth(this.currentDate.getMonth() - 1);
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentDate.setMonth(this.currentDate.getMonth() + 1);
    this.generateCalendar();
  }

  getCurrentMonthYear(): string {
    return this.currentDate.toLocaleDateString('fr-FR', { 
      month: 'long', 
      year: 'numeric' 
    });
  }

  selectDate(day: any): void {
    this.selectedDate = day.fullDate;
    this.selectedDateEvents = this.getEventsForDate(day.fullDate);
  }

  isSelectedDate(day: any): boolean {
    if (!this.selectedDate) return false;
    return day.fullDate.toDateString() === this.selectedDate.toDateString();
  }

  getSelectedDateFormatted(): string {
    if (!this.selectedDate) return '';
    return this.selectedDate.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  // Méthodes de filtrage
  setActiveTab(tabKey: string): void {
    this.activeTab = tabKey;
    this.filterEvents();
  }

  setActiveSection(section: 'appointments' | 'vaccines' | 'services'): void {
    this.activeSection = section;
    this.filterEvents();
  }

  getSectionStats(): string {
    const totalItems = this.paginationInfo.past.totalItems + this.paginationInfo.upcoming.totalItems;
    const upcoming = this.paginationInfo.upcoming.totalItems;
    const completed = this.paginationInfo.past.totalItems;
    return `${totalItems} au total • ${upcoming} à venir • ${completed} terminés`;
  }

  getSectionTitle(): string {
    switch (this.activeSection) {
      case 'appointments':
        return 'Rendez-vous médicaux';
      case 'vaccines':
        return 'Vaccinations';
      case 'services':
        return 'Prestations & Examens médicaux';
      default:
        return 'Événements de santé';
    }
  }

  getSectionIcon(): string {
    switch (this.activeSection) {
      case 'appointments':
        return 'ri-calendar-line';
      case 'vaccines':
        return 'ri-syringe-line';
      case 'services':
        return 'ri-service-line';
      default:
        return 'ri-calendar-line';
    }
  }

  getEmptyStateMessage(): string {
    let baseMessage = "Aucun événement de santé"
    
    // switch (this.activeSection) {
    //   case 'appointments':
    //     baseMessage = 'Aucun rendez-vous médical';
    //     break;
    //   case 'vaccines':
    //     baseMessage = 'Aucune vaccination';
    //     break;
    //   case 'services':
    //     baseMessage = 'Aucune prestation';
    //     break;
    //   default:
    //     baseMessage = 'Aucun événement';
    // }
    
    switch (this.activeTab) {
      case 'upcoming':
        return `${baseMessage} à venir`;
      case 'past':
        return `${baseMessage} passé`;
      default:
        return `${baseMessage} trouvé`;
    }
  }

  filterEvents(): void {
    // Faire confiance à l'API : aucun refiltrage par date.
    // Agenda de santé = tous les événements retournés avec timeFilter=upcoming
    // Histoire de santé = tous les événements retournés avec timeFilter=past
    let events = this.activeTab === 'past' ? [...this.pastEvents] : [...this.upcomingEvents];
    console.log('Filtering events:', {
      activeTab: this.activeTab,
      pastEvents: this.pastEvents.length,
      upcomingEvents: this.upcomingEvents.length,
      eventsForTab: events.length
    });

    // Filtrer selon la section active (type d'événement) uniquement
    if (this.activeSection === 'appointments') {
      events = events.filter(event => event.type === HealthEventType.APPOINTMENT);
    } else if (this.activeSection === 'vaccines') {
      events = events.filter(event => event.type === HealthEventType.VACCINATION);
    } else if (this.activeSection === 'services') {
      events = events.filter(event =>
        event.type === HealthEventType.MEDICAL_SERVICE ||
        event.type === HealthEventType.FINANCIAL_SERVICE ||
        event.type === HealthEventType.EXAMINATION ||
        event.type === HealthEventType.TREATMENT
      );
    }

    this.filteredEvents = events.sort((a, b) => {
      const ta = this.toValidDate(a.date)?.getTime() ?? 0;
      const tb = this.toValidDate(b.date)?.getTime() ?? 0;
      return ta - tb;
    });
    console.log('Final filtered events:', this.filteredEvents.length, this.filteredEvents);
  }

  /**
   * Convertit une valeur en Date valide (retourne null si invalide).
   */
  private toValidDate(value: Date | string | undefined | null): Date | null {
    if (value == null) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  // Méthodes de formatage (gèrent les dates invalides pour éviter RangeError)
  formatDateShort(date: Date | string | undefined): string {
    const d = this.toValidDate(date);
    if (!d) return '';
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short'
    }).format(d);
  }

  formatTime(date: Date | string | undefined): string {
    const d = this.toValidDate(date);
    if (!d) return '';
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  }

  formatTimeString(timeString: string | undefined): string {
    if (!timeString) return '';
    const d = this.toValidDate(timeString);
    if (!d) return '';
    return this.formatTime(d);
  }

  getStartTime(event: HealthEvent): string | undefined {
    return event.appointment?.startTime;
  }

  getEndTime(event: HealthEvent): string | undefined {
    return event.appointment?.endTime;
  }

  /**
   * Obtient la date/heure complète d'un événement en utilisant startTime si disponible
   * pour une comparaison précise (prend en compte l'heure, pas seulement la date)
   */
  getEventDateTime(event: HealthEvent): Date {
    // Pour les rendez-vous, utiliser startTime si disponible pour avoir l'heure précise
    if (event.appointment?.startTime) {
      try {
        return new Date(event.appointment.startTime);
      } catch {
        // Si startTime n'est pas valide, utiliser event.date
        return event.date;
      }
    }
    // Sinon, utiliser la date de l'événement
    return event.date;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'pending_payment':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed':
        return 'Terminé';
      case 'scheduled':
        return 'Confirmé';
      case 'confirmed':
        return 'Confirmé';
      case 'pending':
        return 'En attente';
      case 'pending_payment':
        return 'En attente de paiement';
      case 'rejected':
        return 'Rejeté';
      case 'cancelled':
        return 'Annulé';
      default:
        return 'Inconnu';
    }
  }

  // Méthodes pour le calendrier
  onDateSelected(date: Date) {
    this.selectedDate = date;
    this.selectedDateEvents = this.allEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
  }

  onEventClicked(event: CalendarEvent) {
    // Trouver l'événement de santé correspondant
    const healthEvent = this.allEvents.find(he => he.id === event.id);
    if (healthEvent) {
      this.navigateToEventDetails(healthEvent, 'health-event');
    }
  }

  navigateToEventDetails(
    event: HealthEvent,
    source: 'appointment' | 'health-event' = 'appointment'
  ): void {
    this.router.navigate([event.id], {
      relativeTo: this.route,
      queryParams: {
        source,
        tab: this.activeTab,
      },
      state: { event },
    });
  }

  convertToCalendarEvents(healthEvents: HealthEvent[]): CalendarEvent[] {
    console.log('Converting to calendar events:', healthEvents);
    const calendarEvents = healthEvents.map(event => ({
      id: event.id,
      title: event.title,
      date: new Date(event.date),
      type: event.type,
      color: this.getEventColorForType(event.type),
      description: event.description,
      location: event.location,
      doctor: event.doctor,
      startTime: this.getStartTime(event),
      endTime: this.getEndTime(event)
    }));
    console.log('Converted calendar events:', calendarEvents);
    return calendarEvents;
  }

  private getEventColorForType(type: string): string {
    const colorMap: { [key: string]: string } = {
      [HealthEventType.APPOINTMENT]: 'bg-blue-500',
      [HealthEventType.VACCINATION]: 'bg-green-500',
      [HealthEventType.EXAMINATION]: 'bg-purple-500',
      [HealthEventType.TREATMENT]: 'bg-red-500',
      [HealthEventType.MEDICAL_SERVICE]: 'bg-indigo-500',
      [HealthEventType.FINANCIAL_SERVICE]: 'bg-yellow-500',
      [HealthEventType.OTHER]: 'bg-gray-500',
      'default': 'bg-gray-500'
    };
    return colorMap[type] || colorMap['default'];
  }

  /**
   * Gère les erreurs de chargement d'image
   */
  onImageError(event: any): void {
    const img = event.target as HTMLImageElement;
    const src = img.src;
    console.log('Erreur de chargement de l\'image du docteur:', src);
    this.imageErrors.add(src);
  }

  /**
   * Vérifie si une image a échoué à charger
   */
  hasImageError(src: string): boolean {
    return this.imageErrors.has(src);
  }

  /**
   * Vérifie si un rendez-vous peut être annulé
   */
  canCancelAppointment(event: HealthEvent): boolean {
    // Vérifier si c'est un rendez-vous
    if (event.type !== HealthEventType.APPOINTMENT) {
      return false;
    }

    // Vérifier si le rendez-vous est uniquement en attente
    if (event.status !== 'pending' && event.status !== 'pending_payment') {
      return false;
    }

    // Vérifier si le rendez-vous n'est pas encore passé
    const appointmentDate = new Date(event.date);
    const today = new Date();

    // Réinitialiser l'heure pour comparer seulement les dates
    today.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);

    // Le rendez-vous peut être annulé s'il est dans le futur ou aujourd'hui
    return appointmentDate >= today;
  }

  /**
   * Annule un rendez-vous
   */
  cancelAppointment(event: HealthEvent): void {
    // Fermer le dropdown après l'action
    this.closeDropdown(event.id);
    
    if (!event.appointment || !event.appointment._id) {
      console.error('ID du rendez-vous non trouvé');
      this.swalService.error('Erreur', 'Impossible d\'annuler ce rendez-vous');
      return;
    }

    this.swalService
      .confirm(
        "Confirmer l'annulation",
        'Êtes-vous sûr de vouloir annuler ce rendez-vous ?'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.appointmentService.cancelReservation(event.appointment._id).subscribe({
            next: () => {
              this.swalService.success(
                'Rendez-vous annulé',
                'Le rendez-vous a été annulé avec succès'
              );
              // Recharger les données
              this.loadAppointmentsList();
            },
            error: (error) => {
              console.error("Erreur lors de l'annulation:", error);
              this.swalService.error(
                'Erreur',
                "Impossible d'annuler le rendez-vous"
              );
            },
          });
        }
      });
  }

  /**
   * Ouvre le modal de paiement pour un rendez-vous en attente de paiement
   */
  payAppointment(event: HealthEvent): void {
    this.closeDropdown(event.id);

    if (!event.appointment && !event.paymentId) {
      console.error('Aucune donnée de rendez-vous disponible pour le paiement');
      this.swalService.error('Erreur', 'Impossible de lancer le paiement pour cet événement');
      return;
    }

    const checkoutLink =
      event.appointment?.checkoutLink ||
      event.checkoutLink ||
      event.payment?.checkoutLink ||
      null;

    if (checkoutLink) {
      this.openCheckoutModal(checkoutLink);
      return;
    }

    const paymentId = event.paymentId || event.appointment?.paymentId;
    if (paymentId) {
      this.paymentService.getPaymentDetails(paymentId).subscribe({
        next: (res) => {
          const link = res?.checkoutLink ?? res?.payment?.checkoutLink;
          if (link) {
            this.openCheckoutModal(link);
          } else {
            this.swalService.error('Erreur', 'Lien de paiement indisponible.');
          }
        },
        error: () => {
          this.swalService.error('Erreur', 'Impossible de récupérer les détails du paiement.');
        },
      });
      return;
    }

    const appointmentId = event.appointment?._id;
    if (appointmentId) {
      this.appointmentService.getAppointmentDetails(appointmentId).subscribe({
        next: (res) => {
          const link = res?.checkoutLink ?? res?.payment?.checkoutLink;
          if (link) {
            this.openCheckoutModal(link);
          } else {
            this.swalService.error('Erreur', 'Lien de paiement introuvable pour ce rendez-vous.');
          }
        },
        error: () => {
          this.swalService.error('Erreur', 'Impossible de charger les détails du rendez-vous.');
        },
      });
      return;
    }

    this.swalService.error('Erreur', 'Aucun lien de paiement disponible pour cet événement.');
  }

  canPayAppointment(event: HealthEvent): boolean {
    return event.type === HealthEventType.APPOINTMENT && event.status === 'pending_payment';
  }

  private openCheckoutModal(checkoutLink: string): void {
    this.modalService.open({
      component: CheckoutIframeModalComponent,
      width: '700px',
      closable: true,
      data: { checkoutLink },
    });
  }

  toggleDropdown(eventId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    if (this.openDropdowns.has(eventId)) {
      this.openDropdowns.delete(eventId);
    } else {
      // Fermer tous les autres dropdowns
      this.openDropdowns.clear();
      this.openDropdowns.add(eventId);
    }
  }

  /**
   * Ferme le dropdown pour un événement spécifique
   */
  closeDropdown(eventId: string): void {
    this.openDropdowns.delete(eventId);
  }

  /**
   * Vérifie si le dropdown est ouvert pour un événement
   */
  isDropdownOpen(eventId: string): boolean {
    return this.openDropdowns.has(eventId);
  }

  /**
   * Ferme tous les dropdowns quand on clique ailleurs
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Vérifier si le clic est en dehors des dropdowns
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-container')) {
      this.openDropdowns.clear();
    }
  }

  /**
   * Méthodes de pagination
   */
  goToPage(page: number): void {
    if (page < 1 || page > this.getCurrentPaginationInfo().totalPages) {
      return;
    }

    // Mettre à jour la page courante selon l'onglet actif
    if (this.activeTab === 'past') {
      this.paginationInfo.past.currentPage = page;
    } else {
      this.paginationInfo.upcoming.currentPage = page;
    }

    // Recharger les données
    this.loadAppointmentsList();
  }

  nextPage(): void {
    const currentPagination = this.getCurrentPaginationInfo();
    if (currentPagination.currentPage < currentPagination.totalPages) {
      this.goToPage(currentPagination.currentPage + 1);
    }
  }

  previousPage(): void {
    const currentPagination = this.getCurrentPaginationInfo();
    if (currentPagination.currentPage > 1) {
      this.goToPage(currentPagination.currentPage - 1);
    }
  }

  getCurrentPaginationInfo() {
    return this.activeTab === 'past' ? this.paginationInfo.past : this.paginationInfo.upcoming;
  }

  getPaginationPages(): number[] {
    const currentPagination = this.getCurrentPaginationInfo();
    const totalPages = currentPagination.totalPages;
    const currentPage = currentPagination.currentPage;
    
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const pages: number[] = [];
    const start = Math.max(1, currentPage - 3);
    const end = Math.min(totalPages, currentPage + 3);
    
    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push(-1); // -1 représente "..."
      }
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push(-1); // -1 représente "..."
      }
      pages.push(totalPages);
    }
    
    return pages;
  }
}
